// src/pages/auth/PinCodePage.jsx

import React, {
  useState,
  useRef,
  useEffect
} from 'react';

import {
  useNavigate,
  useLocation
} from 'react-router-dom';

import {
  useDispatch,
  useSelector
} from 'react-redux';

import {
  ArrowLeft,
  Mail,
  Check,
  RefreshCw,
  AlertCircle,
  Loader2
} from 'lucide-react';

import {
  useLanguage
} from '../../contexts/LanguageContext';

import useTranslation from '../../hooks/useTranslation';

import colors from '../../constants/colors';

import styles from '../../styles/PinCodePage.module.css';

// ============================================================
// PIN CODE SLICE
// ============================================================

import {
  sendPin,
  verifyPinAndGetUser,
  regeneratePin,
  getPinStatus,
  resetPinState,
  clearPinErrors,
  resetAttempts,

  incrementAttempts,

  selectPinSending,
  selectPinVerifying,
  selectPinVerifyError,
  selectPinSendError,
  selectPinVerifySuccess,
  selectVerifiedUser,
  selectAttemptsRemaining,
  selectIsLocked,
  selectPinStatusMessage,
  selectSentToEmail,
  selectSentToPhone,
  selectEmailVerified,
  selectPhoneVerified,
  selectIsFullyVerified
} from '../../store/slices/pinCodeSlice';

// ============================================================
// AUTH SLICE
// ============================================================

import {
  selectUser,
  updateUser,
  setEmailVerified,
  setPhoneVerified
} from '../../store/slices/authSlice';

// ============================================================
// CONSTANTS
// ============================================================

const PIN_LENGTH = 6;

// ============================================================
// COMPONENT
// ============================================================

const PinCodePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // ==========================================================
  // LOCAL STATE
  // ==========================================================

  const [pin, setPin] = useState(
    Array(PIN_LENGTH).fill('')
  );

  const [isLoading, setIsLoading] =
    useState(false);

  const [error, setError] =
    useState('');

  const [resendTimer, setResendTimer] =
    useState(0);

  const [success, setSuccess] =
    useState(false);

  const inputRefs =
    useRef([]);

  const verificationRedirected =
    useRef(false);

  // ==========================================================
  // LANGUAGE
  // ==========================================================

  const { currentLanguage } =
    useLanguage();

  // ==========================================================
  // LOCATION STATE
  // ==========================================================

  const email =
    location.state?.email || '';

  const phone =
    location.state?.phone || '';

  const fromRegister =
    location.state?.fromRegister || false;

  const userData =
    location.state?.userData || {};

  const userId =
    location.state?.userId || '';

  const autoSendPin =
    location.state?.autoSendPin || false;

  // ==========================================================
  // PIN CODE REDUX SELECTORS
  // ==========================================================

  const isSending =
    useSelector(selectPinSending);

  const isVerifying =
    useSelector(selectPinVerifying);

  const verifyError =
    useSelector(selectPinVerifyError);

  const sendError =
    useSelector(selectPinSendError);

  const verifySuccess =
    useSelector(selectPinVerifySuccess);

  const verifiedUser =
    useSelector(selectVerifiedUser);

  const attemptsRemaining =
    useSelector(selectAttemptsRemaining);

  const isLocked =
    useSelector(selectIsLocked);

  const statusMessage =
    useSelector(selectPinStatusMessage);

  const sentToEmail =
    useSelector(selectSentToEmail);

  const sentToPhone =
    useSelector(selectSentToPhone);

  const pinEmailVerified =
    useSelector(selectEmailVerified);

  const pinPhoneVerified =
    useSelector(selectPhoneVerified);

  const isFullyVerified =
    useSelector(selectIsFullyVerified);

  // ==========================================================
  // AUTH REDUX
  // ==========================================================

  const currentUser =
    useSelector(selectUser);

  // ==========================================================
  // TRANSLATIONS
  // ==========================================================

  const {
    translatedText: verificationTitle
  } = useTranslation(
    'Email Verification'
  );

  const {
    translatedText: verificationSubtitle
  } = useTranslation(
    `Enter the ${PIN_LENGTH}-digit code sent to your email`
  );

  const {
    translatedText: codeSentTo
  } = useTranslation(
    'Code sent to'
  );

  const {
    translatedText: verifyButton
  } = useTranslation(
    'Verify'
  );

  const {
    translatedText: resendCode
  } = useTranslation(
    'Resend Code'
  );

  const {
    translatedText: resendIn
  } = useTranslation(
    'Resend in'
  );

  const {
    translatedText: seconds
  } = useTranslation(
    's'
  );

  const {
    translatedText: verificationSuccess
  } = useTranslation(
    'Verification Successful!'
  );

  const {
    translatedText: verificationSuccessMessage
  } = useTranslation(
    'Your email has been verified.'
  );

  const {
    translatedText: continueText
  } = useTranslation(
    'Continue'
  );

  const {
    translatedText: verificationFailed
  } = useTranslation(
    'Verification Failed'
  );

  const {
    translatedText: invalidCode
  } = useTranslation(
    'Invalid verification code. Please try again.'
  );

  const {
    translatedText: codeResent
  } = useTranslation(
    'Code Resent'
  );

  const {
    translatedText: codeResentMessage
  } = useTranslation(
    `A new ${PIN_LENGTH}-digit verification code has been sent to your email.`
  );

  const {
    translatedText: pleaseWait
  } = useTranslation(
    'Please wait'
  );

  const {
    translatedText: pinRequired
  } = useTranslation(
    `Please enter the complete ${PIN_LENGTH}-digit verification code`
  );

  const {
    translatedText: tooManyAttempts
  } = useTranslation(
    'Too many failed attempts. Please wait 30 minutes.'
  );

  const {
    translatedText: continueToLogin
  } = useTranslation(
    'Continue to Login'
  );

  const {
    translatedText: phoneVerifiedMessage
  } = useTranslation(
    'Your phone number has been verified successfully!'
  );

  const {
    translatedText: emailVerifiedMessage
  } = useTranslation(
    'Your email has been verified successfully!'
  );

  const {
    translatedText: completeProfile
  } = useTranslation(
    'Complete Your Provider Profile'
  );

  const {
    translatedText: continueToProfile
  } = useTranslation(
    'Continue to Profile Setup'
  );

  // ==========================================================
  // AUTO SEND PIN
  // ==========================================================

  useEffect(() => {
    if (
      autoSendPin &&
      userId
    ) {
      console.log(
        '[PinCodePage] 🔄 Auto-sending PIN for user:',
        userId
      );

      handleSendPin();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    autoSendPin,
    userId
  ]);

  // ==========================================================
  // RESEND TIMER
  // ==========================================================

  useEffect(() => {
    if (resendTimer <= 0) {
      return undefined;
    }

    const timer =
      setInterval(() => {
        setResendTimer(
          previous =>
            previous > 0
              ? previous - 1
              : 0
        );
      }, 1000);

    return () =>
      clearInterval(timer);
  }, [
    resendTimer
  ]);

  // ==========================================================
  // AUTO FOCUS
  // ==========================================================

  useEffect(() => {
    if (
      inputRefs.current[0]
    ) {
      inputRefs.current[0].focus();
    }
  }, []);

  // ==========================================================
  // CHECK PIN STATUS
  // ==========================================================

  useEffect(() => {
    checkPinStatus();

    return () => {
      dispatch(
        resetPinState()
      );
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========================================================
  // CHECK VERIFICATION STATUS
  // ==========================================================

  useEffect(() => {
    if (!verifySuccess) {
      return;
    }

    console.log(
      '================================================'
    );
    console.log(
      '[PinCodePage] ✅ VERIFICATION SUCCESS'
    );
    console.log(
      '[PinCodePage] PIN slice emailVerified:',
      pinEmailVerified
    );
    console.log(
      '[PinCodePage] PIN slice phoneVerified:',
      pinPhoneVerified
    );
    console.log(
      '[PinCodePage] PIN slice fullyVerified:',
      isFullyVerified
    );
    console.log(
      '[PinCodePage] Auth user:',
      currentUser
    );
    console.log(
      '================================================'
    );

    setSuccess(true);

    // AppContent will handle the redirect

  }, [
    verifySuccess,
    pinEmailVerified,
    pinPhoneVerified,
    isFullyVerified,
    currentUser
  ]);

  // ==========================================================
  // CHECK PIN STATUS
  // ==========================================================

  const checkPinStatus = async () => {
    try {
      await dispatch(
        getPinStatus()
      ).unwrap();

    } catch (statusError) {
      console.warn(
        '[PinCodePage] Failed to get PIN status:',
        statusError
      );
    }
  };

  // ==========================================================
  // SEND PIN
  // ==========================================================

  const handleSendPin = async () => {
    if (isSending) {
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      console.log(
        '[PinCodePage] 📤 Sending PIN...'
      );

      const result =
        await dispatch(
          sendPin()
        ).unwrap();

      console.log(
        '[PinCodePage] 📧 Send PIN result:',
        result
      );

      if (
        result?.success
      ) {
        console.log(
          '[PinCodePage] ✅ PIN sent successfully'
        );

        setResendTimer(60);

        setError('');

      } else {
        setError(
          'Failed to send PIN. Please try again.'
        );
      }

    } catch (sendPinError) {
      console.error(
        '[PinCodePage] ❌ Failed to send PIN:',
        sendPinError
      );

      setError(
        typeof sendPinError === 'string'
          ? sendPinError
          : 'Failed to send PIN. Please try again.'
      );

    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================================
  // PIN CHANGE
  // ==========================================================

  const handlePinChange = (
    text,
    index
  ) => {
    if (
      text.length > 1
    ) {
      text =
        text.slice(
          0,
          1
        );
    }

    if (
      text &&
      !/^\d+$/.test(text)
    ) {
      return;
    }

    const newPin = [
      ...pin
    ];

    newPin[index] =
      text;

    setPin(
      newPin
    );

    setError('');

    setSuccess(false);

    dispatch(
      clearPinErrors()
    );

    if (
      text &&
      index <
      PIN_LENGTH - 1
    ) {
      if (
        inputRefs.current[
        index + 1
        ]
      ) {
        inputRefs.current[
          index + 1
        ].focus();
      }
    }

    if (
      text &&
      index ===
      PIN_LENGTH - 1 &&
      newPin.every(
        digit =>
          digit !== ''
      )
    ) {
      const completePin =
        newPin.join('');

      console.log(
        '[PinCodePage] 🔐 Complete PIN entered:',
        completePin
      );

      handleVerify(
        newPin
      );
    }
  };

  // ==========================================================
  // KEY DOWN
  // ==========================================================

  const handleKeyDown = (
    event,
    index
  ) => {
    if (
      event.key ===
      'Backspace' &&
      !pin[index] &&
      index > 0
    ) {
      inputRefs.current[
        index - 1
      ].focus();
    }

    if (
      event.key ===
      'ArrowLeft' &&
      index > 0
    ) {
      inputRefs.current[
        index - 1
      ].focus();
    }

    if (
      event.key ===
      'ArrowRight' &&
      index <
      PIN_LENGTH - 1
    ) {
      inputRefs.current[
        index + 1
      ].focus();
    }
  };

  // ==========================================================
  // PASTE
  // ==========================================================

  const handlePaste = (
    event
  ) => {
    event.preventDefault();

    const pastedData =
      event.clipboardData
        .getData('text');

    const cleanedData =
      pastedData
        .replace(/\D/g, '')
        .slice(
          0,
          PIN_LENGTH
        );

    if (
      cleanedData.length === 0
    ) {
      return;
    }

    const newPin = [
      ...pin
    ];

    for (
      let i = 0;
      i <
      cleanedData.length &&
      i <
      PIN_LENGTH;
      i++
    ) {
      newPin[i] =
        cleanedData[i];
    }

    setPin(
      newPin
    );

    setError('');

    setSuccess(false);

    dispatch(
      clearPinErrors()
    );

    const nextEmptyIndex =
      newPin.findIndex(
        digit =>
          digit === ''
      );

    if (
      nextEmptyIndex !== -1
    ) {
      inputRefs.current[
        nextEmptyIndex
      ]?.focus();

      return;
    }

    if (
      newPin.every(
        digit =>
          digit !== ''
      )
    ) {
      console.log(
        '[PinCodePage] 📋 Complete PIN pasted'
      );

      handleVerify(
        newPin
      );
    }
  };

  // ==========================================================
  // VERIFY PIN
  // ==========================================================

  const handleVerify = async (
    pinOverride = null
  ) => {
    if (
      isLoading ||
      isVerifying
    ) {
      console.log(
        '[PinCodePage] ⚠️ Verification already in progress'
      );

      return;
    }

    if (isLocked) {
      setError(
        tooManyAttempts
      );

      return;
    }

    const pinArray =
      pinOverride ||
      pin;

    const pinCode =
      Array.isArray(
        pinArray
      )
        ? pinArray.join('')
        : String(
          pinArray
        );

    console.log(
      '[PinCodePage] 🔍 Verifying PIN:',
      pinCode
    );

    console.log(
      '[PinCodePage] 🔢 PIN length:',
      pinCode.length
    );

    if (
      pinCode.length !==
      PIN_LENGTH
    ) {
      setError(
        pinRequired
      );

      console.log(
        '[PinCodePage] ❌ Invalid PIN length:',
        pinCode.length
      );

      return;
    }

    if (
      !/^\d{6}$/.test(
        pinCode
      )
    ) {
      setError(
        'PIN must contain only numbers'
      );

      console.log(
        '[PinCodePage] ❌ PIN contains invalid characters'
      );

      return;
    }

    setIsLoading(true);

    setError('');

    setSuccess(false);

    try {
      console.log(
        '[PinCodePage] 📤 Sending PIN to backend:',
        pinCode
      );

      const result =
        await dispatch(
          verifyPinAndGetUser(
            pinCode
          )
        ).unwrap();

      console.log(
        '[PinCodePage] ✅ Verification API response:',
        result
      );

      if (
        result?.success
      ) {
        console.log(
          '[PinCodePage] 🎉 PIN verification successful'
        );

        // Get the user from the response
        const returnedUser =
          result?.data?.user ||
          result?.data?.data?.user ||
          result?.user ||
          result?.data ||
          verifiedUser ||
          {};

        console.log(
          '[PinCodePage] 👤 Backend verified user:',
          returnedUser
        );

        // Extract verification status from backend
        const backendEmailVerified =
          returnedUser.emailVerified === true;

        const backendPhoneVerified =
          returnedUser.phoneVerified === true;

        console.log(
          '[PinCodePage] 📧 Backend emailVerified:',
          backendEmailVerified
        );

        console.log(
          '[PinCodePage] 📱 Backend phoneVerified:',
          backendPhoneVerified
        );

        // ==================================================
        // CRITICAL: Update auth slice with verification status
        // ==================================================

        if (returnedUser._id || returnedUser.id) {
          // Update user with complete data
          dispatch(updateUser({
            ...returnedUser,
            emailVerified: backendEmailVerified,
            phoneVerified: backendPhoneVerified
          }));
        }

        // ALWAYS set verification flags directly
        dispatch(setEmailVerified(backendEmailVerified));
        dispatch(setPhoneVerified(backendPhoneVerified));

        console.log(
          '[PinCodePage] ✅ Auth state updated with verification status'
        );

        console.log(
          '[PinCodePage] ========================================'
        );
        console.log(
          '[PinCodePage] ✅ AUTH STATE UPDATED'
        );
        console.log(
          '[PinCodePage] emailVerified:',
          backendEmailVerified
        );
        console.log(
          '[PinCodePage] phoneVerified:',
          backendPhoneVerified
        );
        console.log(
          '[PinCodePage] both verified:',
          backendEmailVerified &&
          backendPhoneVerified
        );
        console.log(
          '[PinCodePage] ➡️ AppContent will now handle redirect'
        );
        console.log(
          '[PinCodePage] ========================================'
        );

        setSuccess(true);
        setPin(Array(PIN_LENGTH).fill(''));
        setError('');

      } else {
        console.log(
          '[PinCodePage] ❌ Backend returned unsuccessful verification'
        );

        setError(
          result?.message ||
          result?.error ||
          invalidCode
        );
      }

    } catch (verificationError) {
      console.error(
        '[PinCodePage] ❌ Verification error:',
        verificationError
      );

      const errorMessage =
        typeof verificationError ===
          'string'
          ? verificationError
          : verificationError?.message ||
          verificationError?.error ||
          invalidCode;

      setError(
        errorMessage
      );

      dispatch(
        incrementAttempts()
      );

      setPin(
        Array(PIN_LENGTH).fill('')
      );

      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 50);

    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================================
  // RESEND CODE
  // ==========================================================

  const handleResendCode = async () => {
    if (
      resendTimer > 0 ||
      isSending ||
      isLoading
    ) {
      return;
    }

    setIsLoading(true);

    setError('');

    try {
      console.log(
        '[PinCodePage] 🔄 Regenerating PIN...'
      );

      const result =
        await dispatch(
          regeneratePin()
        ).unwrap();

      console.log(
        '[PinCodePage] 📧 Regenerate PIN response:',
        result
      );

      if (
        result?.success
      ) {
        alert(
          `${codeResent}\n\n${codeResentMessage}`
        );

        setResendTimer(
          60
        );

        setPin(
          Array(PIN_LENGTH).fill('')
        );

        setError('');

        setSuccess(false);

        dispatch(
          clearPinErrors()
        );

        dispatch(
          resetAttempts()
        );

        setTimeout(() => {
          inputRefs.current[0]?.focus();
        }, 50);

      } else {
        alert(
          `${verificationFailed}\n\n${result?.error ||
          result?.message ||
          'Failed to resend code'
          }`
        );
      }

    } catch (resendError) {
      console.error(
        '[PinCodePage] ❌ Resend error:',
        resendError
      );

      alert(
        `${verificationFailed}\n\nFailed to resend code. Please try again.`
      );

    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================================
  // DELIVERY INFORMATION
  // ==========================================================

  const getDeliveryInfo = () => {
    const methods = [];

    const userEmail =
      email ||
      currentUser?.email ||
      '';

    const userPhone =
      phone ||
      currentUser?.phone ||
      '';

    if (
      sentToEmail &&
      userEmail
    ) {
      methods.push(
        `📧 ${userEmail}`
      );
    }

    if (
      sentToPhone &&
      userPhone
    ) {
      methods.push(
        `📱 ${userPhone}`
      );
    }

    return methods.length > 0
      ? methods.join(' & ')
      : 'your contact methods';
  };

  // ==========================================================
  // RENDER PIN INPUTS
  // ==========================================================

  const renderPinInputs = () => {
    return (
      <div
        className={
          styles.pinInputsContainer
        }
      >
        {pin.map(
          (
            digit,
            index
          ) => (
            <input
              key={index}

              ref={ref => {
                inputRefs.current[index] =
                  ref;
              }}

              type="text"

              inputMode="numeric"

              pattern="[0-9]"

              className={`
                ${styles.pinInput}
                ${digit ? styles.filled : ''}
                ${error ? styles.error : ''}
              `}

              value={digit}

              onChange={event =>
                handlePinChange(
                  event.target.value,
                  index
                )
              }

              onKeyDown={event =>
                handleKeyDown(
                  event,
                  index
                )
              }

              onPaste={
                handlePaste
              }

              maxLength={1}

              autoFocus={
                index === 0
              }

              aria-label={
                `PIN digit ${index + 1
                }`
              }

              disabled={
                isLoading ||
                isVerifying ||
                isLocked
              }
            />
          )
        )}
      </div>
    );
  };

  // ==========================================================
  // RENDER
  // ==========================================================

  return (
    <div
      className={
        styles.pinContainer
      }
    >
      <div
        className={
          styles.pinCard
        }
      >

        {/* ==================================================
            BACK BUTTON
        ================================================== */}

        <button
          type="button"
          className={
            styles.backButton
          }
          onClick={() =>
            navigate(-1)
          }
          disabled={
            isLoading ||
            isVerifying
          }
        >
          <ArrowLeft
            size={24}
            color={
              colors.textPrimary
            }
          />
        </button>

        {/* ==================================================
            ICON
        ================================================== */}

        <div
          className={
            styles.iconContainer
          }
        >
          <div
            className={
              styles.iconWrapper
            }
          >
            <Mail
              size={60}
              color={
                colors.primary
              }
            />
          </div>
        </div>

        {/* ==================================================
            TITLE
        ================================================== */}

        <div
          className={
            styles.titleContainer
          }
        >
          <h2
            className={
              styles.title
            }
          >
            {verificationTitle}
          </h2>

          <p
            className={
              styles.subtitle
            }
          >
            {verificationSubtitle}
          </p>

          <p
            className={
              styles.emailText
            }
          >
            {codeSentTo}{' '}

            <strong>
              {getDeliveryInfo()}
            </strong>
          </p>

          {statusMessage && (
            <p
              className={`
                ${styles.statusText}
                ${isLocked
                  ? styles.statusTextError
                  : styles.statusTextInfo
                }
              `}
            >
              {statusMessage}
            </p>
          )}
        </div>

        {/* ==================================================
            PIN SECTION
        ================================================== */}

        <div
          className={
            styles.pinSection
          }
        >

          {renderPinInputs()}

          {/* ==================================================
              ERROR
          ================================================== */}

          {(error ||
            verifyError ||
            sendError) && (
              <div
                className={
                  styles.errorMessage
                }
              >
                <AlertCircle
                  size={16}
                  color={
                    colors.error
                  }
                />

                <span
                  className={
                    styles.errorText
                  }
                >
                  {error ||
                    verifyError ||
                    sendError}
                </span>
              </div>
            )}

          {/* ==================================================
              SUCCESS
          ================================================== */}

          {success && (
            <div
              className={
                styles.successMessage
              }
            >
              <Check
                size={16}
                color={
                  colors.success
                }
              />

              <span
                className={
                  styles.successText
                }
              >
                {verificationSuccess}
              </span>
            </div>
          )}

          {/* ==================================================
              LOCKED
          ================================================== */}

          {isLocked && (
            <div
              className={
                styles.lockedMessage
              }
            >
              <AlertCircle
                size={16}
                color={
                  colors.error
                }
              />

              <span
                className={
                  styles.lockedText
                }
              >
                {tooManyAttempts}
              </span>
            </div>
          )}

          {/* ==================================================
              ATTEMPTS
          ================================================== */}

          {!isLocked &&
            attemptsRemaining > 0 &&
            attemptsRemaining < 5 && (
              <p
                className={
                  styles.attemptsText
                }
              >
                {attemptsRemaining}{' '}
                attempt
                {attemptsRemaining !== 1
                  ? 's'
                  : ''}{' '}
                remaining
              </p>
            )}

        </div>

        {/* ==================================================
            VERIFY BUTTON
        ================================================== */}

        <button
          type="button"
          className={`
            ${styles.verifyButton}
            ${pin.some(
            digit =>
              digit === ''
          ) ||
              isLoading ||
              isVerifying ||
              isLocked
              ? styles.disabled
              : ''
            }
          `}
          onClick={() =>
            handleVerify()
          }
          disabled={
            pin.some(
              digit =>
                digit === ''
            ) ||
            isLoading ||
            isVerifying ||
            isLocked
          }
        >
          {isLoading ||
            isVerifying ? (
            <>
              <Loader2
                size={20}
                className={
                  styles.spinner
                }
              />

              <span>
                {pleaseWait}
              </span>
            </>
          ) : (
            <>
              <span>
                {verifyButton}
              </span>

              <Check
                size={20}
              />
            </>
          )}
        </button>

        {/* ==================================================
            RESEND
        ================================================== */}

        <div
          className={
            styles.resendContainer
          }
        >
          {resendTimer > 0 ? (
            <span
              className={
                styles.resendTimer
              }
            >
              {resendIn}{' '}
              {resendTimer}
              {seconds}
            </span>
          ) : (
            <button
              type="button"
              className={
                styles.resendLink
              }
              onClick={
                handleResendCode
              }
              disabled={
                isLoading ||
                isSending ||
                isVerifying ||
                isLocked
              }
            >
              <RefreshCw
                size={16}
                className={
                  styles.resendIcon
                }
              />

              {isSending
                ? 'Sending...'
                : resendCode}
            </button>
          )}
        </div>

        {/* ==================================================
            VERIFICATION INFO
        ================================================== */}

        <div
          className={
            styles.verificationInfo
          }
        >
          <Mail
            size={16}
            color={
              colors.gray500
            }
          />

          <span
            className={
              styles.verificationInfoText
            }
          >
            Code sent to both your email and phone
          </span>
        </div>

        {/* ==================================================
            PROVIDER INFO
        ================================================== */}

        <div
          className={
            styles.providerInfoContainer
          }
        >
          <span
            className={
              styles.providerInfoText
            }
          >
            Registering as: Service Provider
          </span>
        </div>

        {/* ==================================================
            HAITIAN FLAG
        ================================================== */}

        <div
          className={
            styles.flagContainer
          }
        >
          <div
            className={`
              ${styles.flagStripe}
              ${styles.flagBlue}
            `}
          />

          <div
            className={`
              ${styles.flagStripe}
              ${styles.flagRed}
            `}
          />
        </div>

      </div>
    </div>
  );
};

export default PinCodePage;